import React, { useState, useEffect } from 'react';
import { X, Calendar, Clock, FileText, User, Search, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { appointmentAPI } from '../../../api/appointment';

const PatientHistoryModal = ({ isOpen, onClose, patient, doctorId }) => {
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedAppointments, setExpandedAppointments] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('all'); // all, last30days, last90days, thisYear

  useEffect(() => {
    if (isOpen && patient) {
      fetchPatientHistory();
    }
  }, [isOpen, patient]);

  const fetchPatientHistory = async () => {
    if (!patient?._id && !patient?.id) return;

    setLoading(true);
    setError(null);

    try {
      // Use the patient filter from appointments API
      const params = {
        patient: patient._id || patient.id,
        includeDoctor: true,
        includePatient: true,
        sort: '-dateTime', // Most recent first
        limit: 100 // Get last 100 appointments
      };

      // Apply date filtering
      if (dateFilter !== 'all') {
        const now = new Date();
        let fromDate;
        
        switch (dateFilter) {
          case 'last30days':
            fromDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
            break;
          case 'last90days':
            fromDate = new Date(now.getTime() - 90 * 24 * 60 * 60 * 1000);
            break;
          case 'thisYear':
            fromDate = new Date(now.getFullYear(), 0, 1);
            break;
        }
        
        if (fromDate) {
          params.from = fromDate.toISOString().split('T')[0];
        }
      }

      const response = await appointmentAPI.list(params);
      
      if (response.success) {
        setAppointments(response.data?.items || response.data || []);
      } else {
        throw new Error(response.message || 'Failed to fetch patient history');
      }
    } catch (err) {
      console.error('Error fetching patient history:', err);
      setError(err.message || 'Failed to load patient history');
    } finally {
      setLoading(false);
    }
  };

  const toggleExpanded = (appointmentId) => {
    setExpandedAppointments(prev => ({
      ...prev,
      [appointmentId]: !prev[appointmentId]
    }));
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'scheduled':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'cancelled':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'in-progress':
      case 'inprogress':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const filteredAppointments = appointments.filter(appointment => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const complaint = appointment.complaint || '';
    const note = appointment.note || '';
    const doctorName = appointment.doctor?.firstName 
      ? `${appointment.doctor.firstName} ${appointment.doctor.lastName}`
      : appointment.doctor?.name || '';
    
    return complaint.toLowerCase().includes(searchLower) ||
           note.toLowerCase().includes(searchLower) ||
           doctorName.toLowerCase().includes(searchLower);
  });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 text-white">
          <div className="flex justify-between items-center">
            <div className="flex items-center space-x-4">
              <div className="h-12 w-12 bg-white bg-opacity-20 rounded-xl flex items-center justify-center">
                <User className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Patient Medical History</h2>
                <p className="text-blue-100">
                  {patient?.firstName || patient?.name} {patient?.lastName || ''} 
                  {patient?.patientId && ` • ID: ${patient.patientId}`}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Filters and Search */}
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search by complaint, notes, or doctor name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="all">All Time</option>
                <option value="last30days">Last 30 Days</option>
                <option value="last90days">Last 90 Days</option>
                <option value="thisYear">This Year</option>
              </select>
            </div>
            <button
              onClick={fetchPatientHistory}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2"
            >
              <Search className="w-4 h-4" />
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading patient history...</p>
            </div>
          ) : error ? (
            <div className="text-center py-12">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-600 font-medium mb-2">Error Loading History</p>
              <p className="text-gray-600 text-sm">{error}</p>
              <button
                onClick={fetchPatientHistory}
                className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          ) : filteredAppointments.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600 font-medium mb-2">
                {searchTerm ? 'No matching appointments found' : 'No appointment history found'}
              </p>
              <p className="text-gray-500 text-sm">
                {searchTerm 
                  ? 'Try adjusting your search criteria'
                  : 'This patient has no recorded appointments in the system'
                }
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <h3 className="text-lg font-semibold text-gray-900">
                  Appointment History ({filteredAppointments.length})
                </h3>
                <div className="text-sm text-gray-500">
                  Showing {searchTerm ? 'filtered' : 'all'} results
                </div>
              </div>

              {filteredAppointments.map((appointment) => (
                <div
                  key={appointment._id}
                  className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  {/* Appointment Header */}
                  <div className="flex justify-between items-start mb-3">
                    <div className="flex items-center space-x-3">
                      <div className="flex items-center space-x-2">
                        <Calendar className="w-5 h-5 text-blue-600" />
                        <span className="font-medium text-gray-900">
                          {new Date(appointment.dateTime).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric'
                          })}
                        </span>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Clock className="w-4 h-4 text-gray-500" />
                        <span className="text-sm text-gray-600">
                          {new Date(appointment.dateTime).toLocaleTimeString('en-US', {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full border ${getStatusColor(appointment.status)}`}>
                        {appointment.status || 'Unknown'}
                      </span>
                      <button
                        onClick={() => toggleExpanded(appointment._id)}
                        className="p-1 hover:bg-gray-100 rounded"
                      >
                        {expandedAppointments[appointment._id] ? (
                          <ChevronUp className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Doctor Info */}
                  <div className="mb-3">
                    <p className="text-sm text-gray-600">
                      <span className="font-medium">Doctor:</span> Dr. {
                        appointment.doctor?.firstName 
                          ? `${appointment.doctor.firstName} ${appointment.doctor.lastName}`
                          : appointment.doctor?.name || 'Unknown'
                      }
                    </p>
                  </div>

                  {/* Chief Complaint - Always Visible */}
                  {appointment.complaint && (
                    <div className="mb-3 p-3 bg-red-50 border border-red-200 rounded-lg">
                      <div className="flex items-start space-x-2">
                        <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-xs font-semibold text-red-700 uppercase tracking-wide mb-1">
                            Chief Complaint
                          </p>
                          <p className="text-sm text-red-800">{appointment.complaint}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Expanded Details */}
                  {expandedAppointments[appointment._id] && (
                    <div className="mt-4 pt-4 border-t border-gray-200 space-y-3">
                      {appointment.note && (
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Notes
                          </p>
                          <p className="text-sm text-gray-700">{appointment.note}</p>
                        </div>
                      )}
                      
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Duration
                          </p>
                          <p className="text-gray-700">{appointment.durationMin || 30} minutes</p>
                        </div>
                        <div>
                          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                            Created On
                          </p>
                          <p className="text-gray-700">
                            {new Date(appointment.createdAt).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PatientHistoryModal;